import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import Seo from "../components/Seo";
import InfinityMotif from "../components/InfinityMotif";
import { api, formatApiError } from "../lib/api";
import {
  Envelope,
  Phone,
  MapPin,
  ArrowUpRight,
  CheckCircle,
  Star,
} from "@phosphor-icons/react";

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const offerName = searchParams.get("offer") || "";
  const provider = searchParams.get("provider") || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState({ loading: false, sent: false, error: "" });

  // Prefill from URL params on mount / when params change
  useEffect(() => {
    if (offerName) {
      const subject = `Enquiry about: ${offerName}`;
      const intro = provider
        ? `Hi Infinity DMS team,\n\nI'd like more details on the "${offerName}" deal (${provider}).\n\n`
        : `Hi Infinity DMS team,\n\nI'd like more details on the "${offerName}" deal.\n\n`;
      setForm((f) => ({
        ...f,
        subject: f.subject || subject,
        message: f.message || intro,
      }));
    }
  }, [offerName, provider]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, sent: false, error: "" });
    try {
      await api.post("/contact", form);
      setStatus({ loading: false, sent: true, error: "" });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setStatus({ loading: false, sent: false, error: formatApiError(err) });
    }
  };

  return (
    <PublicLayout>
      <Seo
        title="Contact"
        description="Tell us what you need — a new contract, a bill audit, or just a second opinion. We respond within one business day."
        path="/contact"
      />
      <section className="relative pt-20 pb-16 overflow-hidden text-center" data-testid="contact-page">
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-[90vw] h-[320px] rainbow-swoosh pointer-events-none" />
        <InfinityMotif variant="wireframe" className="absolute top-2 left-1/2 -translate-x-1/2 w-[1100px] max-w-[120vw] h-auto" opacity={0.14} />
        <div className="relative max-w-[1000px] mx-auto px-6">
          <div className="flex justify-center mb-7">
            <Star size={24} weight="fill" className="text-[#dda632]" />
          </div>
          <div className="mx-auto mb-8 w-32 h-[5px] rounded-full bg-gradient-to-r from-[#55a2d2] via-[#af3fa6] to-[#dda632]" />
          <div className="eyebrow mb-5">Get in Touch</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.4rem] leading-[0.98]">
            Everything starts<br />
            <span className="rainbow-text">from hello.</span>
          </h1>
          <p className="mt-10 text-xl text-[#a8b2cc] leading-relaxed max-w-2xl mx-auto">
            Tell us what you need — a new contract, a bill audit, or just a second opinion. We
            respond within one business day.
          </p>
        </div>
      </section>

      <div className="rainbow-bar rainbow-bar-xl" />

      <section className="py-20">
        <div className="max-w-[1180px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Contact info sidebar */}
          <aside className="lg:col-span-4 space-y-5">
            <div className="soft-card p-8">
              <div className="eyebrow mb-5">Direct Lines</div>
              <ul className="space-y-5">
                <InfoLine
                  icon={<Phone size={20} weight="duotone" className="text-[#af3fa6]" />}
                  label="Phone"
                  value={
                    <a href="tel:0718889055" className="font-display text-2xl hover:text-[#af3fa6]" data-testid="contact-info-phone">
                      071 888 9055
                    </a>
                  }
                />
                <InfoLine
                  icon={<Envelope size={20} weight="duotone" className="text-[#af3fa6]" />}
                  label="Email"
                  value={
                    <a
                      href="mailto:info@infinity-dms.co.za"
                      className="font-display text-xl hover:text-[#af3fa6] break-all"
                      data-testid="contact-info-email"
                    >
                      info@infinity-dms.co.za
                    </a>
                  }
                />
                <InfoLine
                  icon={<MapPin size={20} weight="duotone" className="text-[#af3fa6]" />}
                  label="Coverage"
                  value={<div className="font-display text-xl">South Africa — nationwide</div>}
                />
              </ul>
            </div>

            <div className="soft-card p-8 bg-[#141e32] text-white border-transparent">
              <div className="eyebrow mb-4" style={{ color: "#dda632" }}>Response Time</div>
              <div className="font-display text-5xl mb-3">&lt; 1 day</div>
              <p className="text-[#a8b2cc] leading-relaxed">
                Every enquiry is read by a human within the hour. Complex quotes come back in under
                24 hours.
              </p>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            <form
              onSubmit={submit}
              className="soft-card p-8 lg:p-12 space-y-6"
              data-testid="contact-form"
            >
              {status.sent && (
                <div className="rounded-2xl bg-[#141e32] border border-[#dda632] p-5 flex items-center gap-3" data-testid="contact-success-message">
                  <CheckCircle size={22} weight="fill" className="text-[#af3fa6]" />
                  <div>
                    <div className="font-display text-lg">Message received.</div>
                    <div className="text-sm text-[#a8b2cc]">We'll be in touch within one business day.</div>
                  </div>
                </div>
              )}
              {status.error && (
                <div className="rounded-2xl border border-[#af3fa6] text-[#af3fa6] bg-[#141e32] p-4 text-sm" data-testid="contact-error-message">
                  {status.error}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Your Name" name="name" value={form.name} onChange={update("name")} required testid="contact-input-name" />
                <Field label="Email" type="email" name="email" value={form.email} onChange={update("email")} required testid="contact-input-email" />
                <Field label="Phone" name="phone" value={form.phone} onChange={update("phone")} testid="contact-input-phone" />
                <Field label="Subject" name="subject" value={form.subject} onChange={update("subject")} testid="contact-input-subject" />
              </div>
              <div>
                <label className="block eyebrow mb-2">Message</label>
                <textarea
                  rows={6}
                  required
                  name="message"
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-input-message"
                  className="w-full rounded-2xl border border-[#2c3854] focus:border-[#f5ecd9] outline-none p-4 bg-[#141e32] resize-none transition-colors"
                  placeholder="Tell us what you're looking for..."
                />
              </div>
              <div className="flex flex-wrap gap-3 items-center pt-1">
                <button type="submit" disabled={status.loading} className="btn-primary disabled:opacity-60" data-testid="contact-submit-button">
                  {status.loading ? "Sending..." : "Send Message"}
                  <ArrowUpRight size={14} weight="bold" />
                </button>
                <span className="eyebrow">We never share your info</span>
              </div>
            </form>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

const InfoLine = ({ icon, label, value }) => (
  <li className="flex items-start gap-3">
    <div className="mt-1">{icon}</div>
    <div>
      <div className="eyebrow mb-1">{label}</div>
      {value}
    </div>
  </li>
);

const Field = ({ label, testid, ...rest }) => (
  <label className="block">
    <span className="block eyebrow mb-2">{label}</span>
    <input
      {...rest}
      data-testid={testid}
      className="w-full rounded-full border border-[#2c3854] focus:border-[#f5ecd9] outline-none px-5 py-3 bg-[#141e32] transition-colors"
    />
  </label>
);
